import axios from 'axios'
import * as common from './common'
import * as api from './api'
import { judgeFeatureClosedByRange } from './util'

/**
 * IoT服务API封装
 */
export class IotService {
  static APP_KEY = '666a659e-244d-46b1-a8cc-3c5190c3823a'
  static APP_PASS = 'd410fca1-7b0a-4522-afca-b3650af14cae'

  static async postIotApi (path, data) {
    const { data: resData } = await axios({
      url: common.iotUrl + path,
      method: 'POST',
      data,
      headers: {
        'content-type': 'application/json'
      }
    })
    return resData
  }

  /**
   * 场景登录：获取验证码（访客/员工）
   * @param {object} params
   * @param {string} params.sid 车场SID
   * @param {string} params.account 手机号
   * @param {number} params.use_scene 访客=1；员工=2
   */
  static async getVerificationCode (params) {
    return this.postIotApi('/api/get_verification_code', {
      app_key: this.APP_KEY,
      app_pass: this.APP_PASS,
      sid: params.sid,
      account: params.account,
      use_scene: params.use_scene
    })
  }

  /**
   * 场景登录：验证码登录（访客/员工）
   * @param {object} params
   * @param {string} params.sid 车场SID
   * @param {string} params.account 手机号
   * @param {number} params.use_scene 访客=1；员工=2
   * @param {string} params.code 验证码
   */
  static async verificationCodeLogin (params) {
    return this.postIotApi('/api/verification_code_login', {
      app_key: this.APP_KEY,
      app_pass: this.APP_PASS,
      sid: params.sid,
      account: params.account,
      use_scene: params.use_scene,
      code: params.code
    })
  }

  /**
   * 获取部门列表（访客/员工 scene token）
   */
  static async getDeptListBySceneToken (accessToken, sid) {
    return this.postIotApi('/scene_api/enterprise.personnel.org.get_list', {
      app_key: this.APP_KEY,
      app_pass: this.APP_PASS,
      access_token: accessToken,
      version: 'v2',
      sid: sid,
      is_need_personnel_count: false
    })
  }

  /**
   * 获取预约审批设置（访客 scene token）
   */
  static async reservationApproveQueryBySceneToken (accessToken, sid) {
    return this.postIotApi('/scene_api/advanced_setting.reservation_approve.query', {
      app_key: this.APP_KEY,
      app_pass: this.APP_PASS,
      access_token: accessToken,
      version: 'v2',
      sid: sid
    })
  }

  /**
   * 获取被访人列表（customer 态人员接口）
   */
  static async getPersonnelListByDept (accessToken, customerId, deptId) {
    return this.postIotApi('/api/enterprise.personnel.get_list', {
      app_key: this.APP_KEY,
      app_pass: this.APP_PASS,
      access_token: accessToken,
      version: 'v2',
      customer_id: customerId,
      page_no: 1,
      page_size: 200,
      department_id_list: [deptId],
      expired: false
    })
  }

  /**
   * 提交预约申请（访客 scene token）
   */
  static async reservationAddBySceneToken (accessToken, sid, payload, isFirstTime) {
    return this.postIotApi('/scene_api/enterprise.reservation.add', {
      app_key: this.APP_KEY,
      app_pass: this.APP_PASS,
      access_token: accessToken,
      version: 'v2',
      sid: sid,
      full_name: payload.full_name,
      phone_number: payload.phone_number,
      plate_number: payload.plate_number,
      email: payload.email || undefined,
      id_card: payload.id_card || undefined,
      reason: payload.reason,
      interviewee_department_id: payload.interviewee_department_id,
      interviewee_full_name: payload.interviewee_full_name,
      reservation_start: payload.reservation_start,
      reservation_end: payload.reservation_end,
      is_first_time: isFirstTime
    })
  }

  /**
   * 员工端：获取待审批预约列表
   */
  static async reservationGetListForAudit (accessToken, sid, params) {
    return this.postIotApi('/scene_api/enterprise.reservation.get_list', {
      app_key: this.APP_KEY,
      app_pass: this.APP_PASS,
      access_token: accessToken,
      version: 'v2',
      sid: sid,
      page_no: params.page_no || 1,
      page_size: params.page_size || 50,
      start_time: params.start_time,
      end_time: params.end_time,
      audit_status_list: Array.isArray(params.audit_status_list) ? params.audit_status_list : [1]
    })
  }

  /**
   * 员工端：预约通过
   */
  static async reservationApproveBySceneToken (accessToken, sid, id, isFirstTime) {
    return this.postIotApi('/scene_api/enterprise.reservation.approve', {
      app_key: this.APP_KEY,
      app_pass: this.APP_PASS,
      access_token: accessToken,
      version: 'v2',
      sid: sid,
      id: id,
      is_first_time: isFirstTime
    })
  }

  /**
   * 员工端：预约拒绝
   */
  static async reservationRejectBySceneToken (accessToken, sid, id, rejectReason) {
    return this.postIotApi('/scene_api/enterprise.reservation.reject', {
      app_key: this.APP_KEY,
      app_pass: this.APP_PASS,
      access_token: accessToken,
      version: 'v2',
      sid: sid,
      id: id,
      reject_reason: rejectReason || undefined
    })
  }

  /**
   * 获取逃费追缴配置
   * @param {string} sid - 会话ID
   * @param {string} version - 版本号
   * @returns {Promise<object>} API响应数据
   */
  static async getEscapeConfig (sid, version = 'v2') {
    const { data } = await axios({
      url: common.iotUrl + '/uuid/' + api.urlList.advanced_setting.vh_fee_order_escape.query,
      method: 'POST',
      data: {
        sid: sid,
        version: version
      },
      header: {
        'content-type': 'application/json'
      }
    })
    return data
  }

  /**
   * 根据SID获取功能特性
   * @param {string} sid - 会话ID
   * @returns {Promise<object>} API响应数据
   */
  static async getFeatureBySid (sid) {
    const { data } = await axios({
      url: common.iotUrl + '/s2p/' + 'sid.feature.get_by_sid',
      method: 'POST',
      data: {
        sid: sid,
        version: 'v2'
      },
      header: {
        'content-type': 'application/json'
      }
    })
    return data
  }

  /**
   * 访客相关：按 sid 查询功能配置（iot /api）
   * @param {string} sid 会话ID
   * @returns {Promise<object>} API响应数据
   */
  static async getVisitorFeatureBySid (sid) {
    return this.postIotApi('/uuid/sid.feature.get_by_sid', {
      version: 'v2',
      sid: sid
    })
  }

  /**
   * 访客功能开关是否开启
   * 按 getVisitorFeatureBySid 的“总开关”字段判断
   * @param {object} featureData getVisitorFeatureBySid 返回值
   * @returns {boolean}
   */
  static isVisitorFeatureEnabled (featureData) {
    if (!featureData || featureData.code !== 10000) return false
    const info = featureData.info || {}
    const reservation = info.reservation || {}
    return Number(reservation.enable) === 1
  }

  static hasFeatureLocationCodes (featureData) {
    if (!featureData) return false
    const areaCode = featureData.area_code
    const cityCode = featureData.city_code
    const hasValue = (val) => val !== null && val !== undefined && String(val).trim() !== ''
    return hasValue(areaCode) && hasValue(cityCode)
  }

  /**
   * P云特惠券包是否开启
   * 条件：area_code/city_code 同时存在，且当前不在 pyun_ad_coupon_closed 关闭期内
   * @param {object} featureData getFeatureBySid 返回值
   * @returns {boolean}
   */
  static isPyunAdCouponEnabled (featureData) {
    if (!featureData || featureData.code !== 10000) return false
    if (!this.hasFeatureLocationCodes(featureData)) return false
    const info = featureData.info || {}
    return !judgeFeatureClosedByRange(info.pyun_ad_coupon_closed)
  }

  /**
   * 花瓣欠费是否开启
   * 条件：当前不在 huaban_escape_closed 关闭期内
   * @param {object} featureData getFeatureBySid 返回值
   * @returns {boolean}
   */
  static isHuabanEscapeEnabled (featureData) {
    if (!featureData || featureData.code !== 10000) return false
    const info = featureData.info || {}
    return !judgeFeatureClosedByRange(info.huaban_escape_closed)
  }

  /**
   * 获取广告配置
   * @param {string} sid - 会话ID
   * @returns {Promise<object>} 广告配置信息
   */
  static async queryADConfig (sid) {
    const data = await this.getFeatureBySid(sid)

    if (data.code === 10000) {
      console.log(data, '获取关闭广告配置')
      return data.info
    }
    throw new Error({ code: data.code, msg: data.msg })
  }

  /**
   * 查询进出场详情
   * @param {object} params - 查询参数
   * @returns {Promise<object>} API响应数据
   */
  static async queryInOutDetail (params) {
    const { data } = await axios({
      url: common.iotUrl + '/s2p/' + api.urlList.query_in_out_detail,
      method: 'POST',
      data: params,
      header: {
        'content-type': 'application/json'
      }
    })
    return data
  }

  /**
   * 通用UUID接口调用
   * @param {string} uuid - UUID
   * @param {object} params - 请求参数
   * @returns {Promise<object>} API响应数据
   */
  static async callUuidApi (uuid, params) {
    const { data } = await axios({
      url: common.iotUrl + '/uuid/' + uuid,
      method: 'POST',
      data: params,
      header: {
        'content-type': 'application/json'
      }
    })
    return data
  }

  /**
   * 通用S2P接口调用
   * @param {string} endpoint - 端点
   * @param {object} params - 请求参数
   * @returns {Promise<object>} API响应数据
   */
  static async callS2pApi (endpoint, params) {
    const { data } = await axios({
      url: common.iotUrl + '/s2p/' + endpoint,
      method: 'POST',
      data: params,
      header: {
        'content-type': 'application/json'
      }
    })
    return data
  }
}
